import { useTheme } from 'bold-ui'
import {
  Chart,
  ChartBody,
  ChartContainer,
  ChartFooter,
  DateRange,
  ReferenceArea,
  ValueRange,
} from 'bold-ui/lib/components/Chart'
import { TickProps } from 'bold-ui/lib/components/Chart/Tick'
import { green, red } from 'bold-ui/lib/styles/colors'
import { SexoEnum } from 'graphql/types.generated'
import moment, { Duration, Moment } from 'moment'
import { useCallback } from 'react'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { reduceMedicoesIguais } from '../../util-medicoes'
import { getReferenceAreasCalculators, ReferenceAreasCalculator } from './referenceAreaCalculators'
import { TickIdade } from './TickIdade'
import { TooltipGraficoAntropometria } from './TooltipGraficoAntropometria'
import { buildAreasPreTermo, dataNascimentoPreTermo, PeriodoPreTermo } from './util-grafico-pre-termo'

export interface GraficoPerimetroCefalicoProps {
  medicoes: HistoricoMedicaoModel[]
  dataNascimento: Moment
  dataRange: DateRange
  sexo: SexoEnum
  periodoPreTermo?: Readonly<PeriodoPreTermo>
  shouldRenderPreTermo?: boolean
}

// TODO@CODEBENDERS - Adicionar funcionalidade de outliers #23674
export function GraficoPerimetroCefalicoView(props: Readonly<GraficoPerimetroCefalicoProps>) {
  const { medicoes, dataNascimento, dataRange, sexo, periodoPreTermo, shouldRenderPreTermo = false } = props

  const theme = useTheme()
  const dataNascimentoAjustada =
    shouldRenderPreTermo && periodoPreTermo ? dataNascimentoPreTermo(periodoPreTermo) : moment(dataNascimento)
  const maxIdadeNoRange = moment.duration(moment(dataRange.end).diff(dataNascimentoAjustada))
  const medicoesComPerimetroCefalico = reduceMedicoesIguais(medicoes, 'valorPerimetroCefalico') ?? []
  const seriesData = medicoesComPerimetroCefalico.map((m) => ({
    x: m.dataMedicao,
    y: m.valorPerimetroCefalico,
  }))
  const yRange = shouldRenderPreTermo ? yRangePreTermo(sexo) : yRangePadrao

  const renderRangeAreas = () => {
    return !shouldRenderPreTermo && periodoPreTermo ? buildAreasPreTermo(periodoPreTermo, dataRange) : []
  }

  const tickRenderer = useCallback(
    (tickProps: TickProps) => (
      <TickIdade {...tickProps} dataNascimento={dataNascimentoAjustada} shouldRenderPreTermo={shouldRenderPreTermo} />
    ),
    [dataNascimentoAjustada, shouldRenderPreTermo]
  )

  const tituloEixoX = shouldRenderPreTermo ? 'Semanas' : 'Idade (meses completos e anos)'

  return (
    <ChartContainer>
      <ChartBody height={500}>
        <Chart<Moment>
          outliers='auto'
          series={[{ name: 'Perímetro cefálico', data: seriesData, color: theme.pallete.gray.c20 }]}
          referenceAreas={getReferenceAreas(
            sexo,
            dataRange,
            maxIdadeNoRange,
            dataNascimentoAjustada,
            shouldRenderPreTermo
          )}
          xAxis={{
            title: tituloEixoX,
            domain: dataRange,
            tickRenderer,
          }}
          rangeAreas={renderRangeAreas()}
          yAxis={{ title: 'Perímetro cefálico', unit: 'cm', domain: yRange }}
          showLegend={false}
          tooltip={{
            type: 'point',
            render: (points) => (
              <TooltipGraficoAntropometria
                label='Perímetro cefálico'
                unidadeDeMedida='cm'
                points={points}
                dataNascimento={dataNascimento}
                shouldRenderPreTermo={shouldRenderPreTermo}
              />
            ),
          }}
        />
      </ChartBody>
      <ChartFooter>{getFooterText(sexo, maxIdadeNoRange, shouldRenderPreTermo)}</ChartFooter>
    </ChartContainer>
  )
}

function getFooterText(sexo: SexoEnum, idadeNoRange: Duration, shouldRenderPreTermo: boolean): string {
  if (shouldRenderPreTermo) {
    return 'Fonte: INTERGROWTH-21ˢᵗ - 2026.'
  }

  if (idadeNoRange.years() > 2) {
    return ''
  }

  switch (sexo) {
    case SexoEnum.MASCULINO:
      return 'Fonte: Caderneta de Saúde da Criança Menino - 2020.'
    case SexoEnum.FEMININO:
      return 'Fonte: Caderneta de Saúde da Criança Menina - 2020.'
    default:
      return ''
  }
}

const yRangePreTermo = (sexo: SexoEnum): ValueRange => {
  if (sexo === SexoEnum.MASCULINO) return { init: 19, end: 48, step: 1 }
  return { init: 18, end: 47, step: 1 }
}

const yRangePadrao: ValueRange = { init: 30, end: 53, step: 1 }

function getReferenceAreas(
  sexo: SexoEnum,
  xRange: DateRange,
  idadeNoRange: Duration,
  dataNascimento: Moment,
  shouldRenderPreTermo: boolean
): ReferenceArea<Moment>[] {
  const refAreaCalculators = getReferenceAreasCalculators(
    sexo,
    dataNascimento,
    xRange,
    'perimetroCefalico',
    shouldRenderPreTermo
  )

  if (shouldRenderPreTermo) {
    return [
      getReferenceAreaPreTermoRiscoInferior(refAreaCalculators),
      getReferenceAreaPreTermoAdequado(refAreaCalculators),
      getReferenceAreaPreTermoRiscoSuperior(refAreaCalculators),
    ]
  }

  if (idadeNoRange.years() <= 2) {
    return [
      getReferenceAreaAbaixoEsperado(refAreaCalculators),
      getReferenceAreaAdequado(refAreaCalculators),
      getReferenceAreaAcimaEsperado(refAreaCalculators),
    ]
  }

  return undefined
}

function getReferenceAreaAbaixoEsperado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: {
      name: 'Abaixo do esperado',
    },
    color: red.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    label: {
      name: 'Adequado',
    },
    color: green.c90,
    stroke: {
      color: red.c60,
    },
    tick: {
      color: green.c60,
    },
  }
}

function getReferenceAreaAcimaEsperado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    label: {
      name: 'Acima do esperado',
    },
    color: red.c90,
    stroke: {
      show: false,
    },
    tick: {
      color: red.c60,
    },
  }
}

function getReferenceAreaPreTermoRiscoInferior(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(-2),
    })),
    label: { name: 'Risco', color: red.c50 },
    color: red.c90,
    stroke: { color: red.c60 },
    tick: { color: red.c60 },
  }
}

function getReferenceAreaPreTermoAdequado(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date, referenceAreaCalculator }) => ({
      x: date,
      upperLimit: referenceAreaCalculator(2),
    })),
    label: { name: 'Adequado', color: green.c40 },
    color: green.c90,
    stroke: { color: red.c60 },
    tick: { color: green.c60 },
  }
}

function getReferenceAreaPreTermoRiscoSuperior(getsPercentis: ReferenceAreasCalculator[]): ReferenceArea<Moment> {
  return {
    area: getsPercentis.map(({ date }) => ({
      x: date,
    })),
    // Se houver dois ReferenceAreas com a mesma label, o grafico se desconfigura.
    // Usamos um caractere invisivel (U+200B) para diferenciar, pois a label oficial
    // nao pode ser alterada. Isso sera resolvido na issue #31237.
    label: { name: 'Risco\u200B', color: red.c50 },
    color: red.c90,
    stroke: { color: 'none' },
    tick: { color: red.c60 },
  }
}
